import { z } from 'zod';
import { ERROR_CODES } from './errors.js';

/**
 * The response envelope defined by FRS §8.6.2 / §8.6.3.
 *
 * Two invariants the whole API depends on:
 *  1. `errors` is NEVER present alongside `data`.
 *  2. Absent optional values are returned as `null`, never omitted, so a client
 *     can tell "not set" from "not returned".
 */

export const errorItemSchema = z.object({
  /** Machine-readable, stable, never localised. Clients branch on this. */
  code: z.enum(ERROR_CODES as [string, ...string[]]),
  /** Human-readable, localised to the caller's language. Display only. */
  message: z.string(),
  /** Present for validation failures: the offending field's path. */
  field: z.string().nullable().default(null),
});
export type ErrorItem = z.infer<typeof errorItemSchema>;

export const paginationMetaSchema = z.object({
  page: z.int().positive(),
  page_size: z.int().positive(),
  total_items: z.int().nonnegative(),
  total_pages: z.int().nonnegative(),
});
export type PaginationMeta = z.infer<typeof paginationMetaSchema>;

/**
 * Correlation id, echoed in every response and emitted on every log line, so a
 * user-reported failure can be traced (FRS §8.6.3, §8.3).
 */
export const baseMetaSchema = z.object({
  request_id: z.string(),
});

export const errorResponseSchema = z.object({
  errors: z.array(errorItemSchema).min(1),
  meta: baseMetaSchema,
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;

/** `{ data: T, meta: { request_id } }` — a single resource. */
export function resourceResponse<T extends z.ZodType>(data: T) {
  return z.object({
    data,
    meta: baseMetaSchema,
  });
}

/** `{ data: T[], meta: { request_id, page, ... } }` — a paginated collection. */
export function collectionResponse<T extends z.ZodType>(item: T) {
  return z.object({
    data: z.array(item),
    meta: baseMetaSchema.extend(paginationMetaSchema.shape),
  });
}

export type ResourceResponse<T> = { data: T; meta: { request_id: string } };
export type CollectionResponse<T> = {
  data: T[];
  meta: { request_id: string } & PaginationMeta;
};
